import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Box,
  Icon,
  Input,
  Radio,
  RadioGroup,
  Text as ChakraText,
  VStack,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlineUserAdd } from 'react-icons/ai'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type { UserErrorResponse } from '../../interfaces'
import { UserContext } from '../../providers'

interface Props {
  childAccountClientNumber: string
  isOpen: boolean
  onClose(): void
  getData(): Promise<void>
}

interface FormProps {
  userName: string
  userEmail: string
  roles: string
  childAccountClientNumber: string
}

const AddNewAgencyChildUserModal = ({
  childAccountClientNumber,
  isOpen,
  onClose,
  getData,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const { user } = useContext(UserContext)

  const toast = useToast()

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{
            userName: '',
            userEmail: '',
            roles: '',
            childAccountClientNumber,
          }}
          onSubmit={async (values): Promise<void> => {
            if (user?.clientNumber) {
              const response = (await request.post(
                `/api/clients/${user.clientNumber}/agency/child/${childAccountClientNumber}/user`,
                {
                  userName: values.userName,
                  userEmail: values.userEmail,
                  roles: values.roles,
                },
              )) as UserErrorResponse | {}

              if (request.error) {
                toast({
                  description: (response as UserErrorResponse).message,
                  status: 'error',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              } else {
                toast({
                  description: t('successfully_added', 'Successfully added'),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })

                await getData()
              }

              onClose()
            }
          }}
          validationSchema={Yup.object().shape({
            userName: Yup.string().required('Required'),
            userEmail: Yup.string().email().required('Required'),
            roles: Yup.mixed()
              .oneOf(['2', '6', '7', '8', '9'], 'Must be a valid user role')
              .required('Required'),
          })}
        >
          {({ isSubmitting, submitForm }) => (
            <>
              <ModalHeader>
                <Icon
                  as={AiOutlineUserAdd}
                  color={colors.appBattleshipGrey}
                  backgroundColor={colors.appPaleGrey}
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="50%"
                  marginRight="0.5em"
                />
                {t('add_user', 'Add User')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4} alignItems="flex-start">
                  <Field name="userName">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.userName && form.touched.userName,
                        )}
                        isRequired
                      >
                        <FormLabel>{t('name', 'Name')}</FormLabel>
                        <ChakraText
                          color={colors.appBattleshipGrey}
                          marginBottom="0.5em"
                        >
                          {t(
                            'name_of_user_associated',
                            'Name of the user that will be associated with this account.',
                          )}
                        </ChakraText>
                        <Input
                          {...field}
                          placeholder={t(
                            'name_of_the_user',
                            'Name of the user',
                          )}
                        />
                        <FormErrorMessage>
                          {form.errors.userName}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="userEmail">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.userEmail && form.touched.userEmail,
                        )}
                        isRequired
                      >
                        <FormLabel>{t('user_email', 'User Email')}</FormLabel>
                        <ChakraText
                          color={colors.appBattleshipGrey}
                          marginBottom="0.5em"
                        >
                          {t(
                            'email_id_associated',
                            'Email Id of the user that will be associated with this account.',
                          )}
                        </ChakraText>
                        <Input
                          {...field}
                          placeholder={t('email_id', 'Email Id')}
                        />
                        <FormErrorMessage>
                          {form.errors.userEmail}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="roles">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.roles && form.touched.roles,
                        )}
                        isRequired
                      >
                        <FormLabel>{t('user_role', 'User Role')}</FormLabel>
                        <ChakraText
                          color={colors.appBattleshipGrey}
                          marginBottom="0.5em"
                        >
                          {t(
                            'select_user_role',
                            "Select the role that best suits user's profile.",
                          )}
                        </ChakraText>
                        <RadioGroup {...field} value={form.values.roles}>
                          <VStack
                            spacing="1em"
                            direction="column"
                            alignItems="flex-start"
                          >
                            <Box>
                              <Radio value="2" onChange={form.handleChange}>
                                {t(
                                  'account_administrator',
                                  'Account Administrator',
                                )}
                              </Radio>
                              <ChakraText
                                color={colors.appBattleshipGrey}
                                paddingLeft="1.5em"
                              >
                                {t(
                                  'account_administrator_description',
                                  'Full access to everything including user management',
                                )}
                              </ChakraText>
                            </Box>
                            <Box>
                              <Radio value="6" onChange={form.handleChange}>
                                {t('developer', 'Developer')}
                              </Radio>
                              <ChakraText
                                color={colors.appBattleshipGrey}
                                paddingLeft="1.5em"
                              >
                                {t(
                                  'developer_description',
                                  'Access to everything except user management and billing',
                                )}
                              </ChakraText>
                            </Box>
                            <Box>
                              <Radio value="8" onChange={form.handleChange}>
                                {t(
                                  'media_library_full_access',
                                  'Media Library Full Access',
                                )}
                              </Radio>
                              <ChakraText
                                color={colors.appBattleshipGrey}
                                paddingLeft="1.5em"
                              >
                                {t(
                                  'media_library_full_access_description',
                                  'Full access to media-library',
                                )}
                              </ChakraText>
                            </Box>
                            <Box>
                              <Radio value="9" onChange={form.handleChange}>
                                {t(
                                  'media_library_view_only_access',
                                  'Media Library View-Only Access',
                                )}
                              </Radio>
                              <ChakraText
                                color={colors.appBattleshipGrey}
                                paddingLeft="1.5em"
                              >
                                {t(
                                  'media_library_view_only_access_description',
                                  'View only access to media library',
                                )}
                              </ChakraText>
                            </Box>
                            <Box>
                              <Radio value="7" onChange={form.handleChange}>
                                {t('finance', 'Finance')}
                              </Radio>
                              <ChakraText
                                color={colors.appBattleshipGrey}
                                paddingLeft="1.5em"
                              >
                                {t(
                                  'finance_description',
                                  'Access to analytics and billing',
                                )}
                              </ChakraText>
                            </Box>
                          </VStack>
                        </RadioGroup>
                        <FormErrorMessage>{form.errors.roles}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button onClick={onClose} variant="outline">
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default AddNewAgencyChildUserModal
